import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { confirmPasswordReset, EmailAuthProvider, onAuthStateChanged, reauthenticateWithCredential, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, verifyPasswordResetCode, type User } from 'firebase/auth'
import { auth, firebaseConfigured } from '../lib/firebase'

interface SessionUser {
  uid: string
  email: string
  name: string
  verified: boolean
}

interface AuthContextValue {
  user: SessionUser | null
  loading: boolean
  firebaseConfigured: boolean
  signIn: (email: string, password: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  verifyResetCode: (code: string) => Promise<string>
  confirmReset: (code: string, password: string) => Promise<void>
  confirmPassword: (password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function developmentPreviewUser(): SessionUser | null {
  if (!import.meta.env.DEV || new URLSearchParams(window.location.search).get('previewWorkspace') !== '1') return null
  return { uid: 'local-preview', email: 's22244@lsc.hk', name: 'Gavin Mak', verified: true }
}

function toSessionUser(user: User): SessionUser {
  return {
    uid: user.uid,
    email: user.email ?? '',
    name: user.displayName ?? user.email?.split('@')[0] ?? 'Editorial Board member',
    verified: user.emailVerified,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const previewUser = useMemo(developmentPreviewUser, [])
  const [user, setUser] = useState<SessionUser | null>(previewUser)
  const [loading, setLoading] = useState(firebaseConfigured && !previewUser)

  useEffect(() => {
    if (!auth || previewUser) return
    return onAuthStateChanged(auth, firebaseUser => {
      setUser(firebaseUser ? toSessionUser(firebaseUser) : null)
      setLoading(false)
    })
  }, [previewUser])

  const handleSignIn = useCallback(async (email: string, password: string) => {
    if (!auth) {
      throw new Error('Firebase Authentication is not configured.')
    }
    await signInWithEmailAndPassword(auth, email, password)
  }, [])

  const handleResetPassword = useCallback(async (email: string) => {
    if (!auth) return
    await sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/#/`,
      handleCodeInApp: false,
    })
  }, [])

  const handleVerifyResetCode = useCallback(async (code: string) => {
    if (!auth) throw new Error('Firebase Authentication is unavailable.')
    return verifyPasswordResetCode(auth, code)
  }, [])

  const handleConfirmReset = useCallback(async (code: string, password: string) => {
    if (!auth) throw new Error('Firebase Authentication is unavailable.')
    await confirmPasswordReset(auth, code, password)
  }, [])

  const handleConfirmPassword = useCallback(async (password: string) => {
    const currentUser = auth?.currentUser
    if (!currentUser?.email) throw new Error('Your Firebase sign-in session is unavailable. Sign in again before continuing.')
    await reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, password))
  }, [])

  const handleSignOut = useCallback(async () => {
    if (auth) await signOut(auth)
    else setUser(null)
  }, [])

  const value = useMemo(() => ({ user, loading, firebaseConfigured, signIn: handleSignIn, resetPassword: handleResetPassword, verifyResetCode: handleVerifyResetCode, confirmReset: handleConfirmReset, confirmPassword: handleConfirmPassword, signOut: handleSignOut }), [user, loading, handleSignIn, handleResetPassword, handleVerifyResetCode, handleConfirmReset, handleConfirmPassword, handleSignOut])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
